/**
 * useImmersiveSwipe
 * Physical finger-follow gesture engine for the immersive catalog's vertical
 * item navigation (the Reels/TikTok mental model). Replaces the old
 * fire-on-release swipe: the pane track now translates 1:1 with the finger,
 * and release physics decide whether to COMMIT to the neighbor or spring back.
 *
 *   swipe UP   (finger up,   offset < 0) = NEXT
 *   swipe DOWN (finger down, offset > 0) = PREV
 *
 * One gesture, start to finish:
 *   touchstart -> record the origin (ignored on interactive targets, while a
 *                 settle animation is running, and wherever `shouldIgnore`
 *                 says the target owns its own scrolling).
 *   touchmove  -> lock the axis once |deltaY| clearly dominates |deltaX|, then
 *                 follow the finger 1:1. Dragging toward a MISSING neighbor
 *                 (catalog edge) applies rubber-band resistance instead.
 *   touchend   -> COMMIT when the drag passed COMMIT_RATIO of the pane height
 *                 OR was a fast flick (COMMIT_VELOCITY, past a small distance
 *                 floor, in the drag direction); otherwise spring back to 0.
 *   commit     -> animate one full pane, then fire onCommit (the host swaps
 *                 the item) and re-center the track to 0 with the transition
 *                 DISABLED in the same patch — standard carousel re-centering,
 *                 the incoming pane simply "becomes" the current one.
 *
 * `commit(dir)` runs the same settle animation programmatically, so wheel /
 * keyboard / on-screen chevrons share the exact spring the finger gets.
 *
 * Reduced motion: no finger-follow at all — the previous threshold-fire
 * behavior with an instant item swap (no springs, no slides).
 *
 * Interactive targets (inputs, buttons, etc.) are ignored so steppers/typing
 * are never hijacked as swipes.
 */
import { ref } from 'vue'

// ── Gesture tuning ───────────────────────────────────────────────
const LOCK_DISTANCE = 8 // px of travel before the axis-lock decision
const LOCK_DOMINANCE = 1.2 // |dy| must exceed |dx| * this to lock vertical
const COMMIT_RATIO = 0.24 // fraction of pane height that commits on release
const COMMIT_VELOCITY = 0.5 // px/ms release flick that also commits...
const MIN_COMMIT_DISTANCE = 40 // ...but only past this distance floor
const EDGE_RESISTANCE = 0.35 // rubber-band factor when no neighbor exists
const SETTLE_MS = 280 // commit/spring duration — MUST match .imm-track.is-settling
const SETTLE_BUFFER_MS = 60 // wait a hair past the CSS transition before swapping
const NUDGE_PX = 18 // programmatic edge-nudge travel (bounce)
const NUDGE_HOLD_MS = 140 // nudge out-phase before springing back

// Reduced-motion fallback: the previous fire-on-release thresholds, unchanged.
const RM_SWIPE_THRESHOLD = 50
const RM_DIRECTION_RATIO = 1.5
const RM_MAX_DURATION = 800

const INTERACTIVE_SELECTOR = 'input, button, textarea, select, a, [data-no-swipe]'

// Shared helper — the intro/hint choreography and the celebrations use it too.
export function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * @param {Object}   deps
 * @param {Function} deps.getPaneHeight    () => px height of one pane (the surface).
 * @param {Function} deps.canNext          () => true when a next item exists.
 * @param {Function} deps.canPrev          () => true when a prev item exists.
 * @param {Function} [deps.shouldIgnore]   (target) => true to leave the touch to
 *                                         the target (e.g. a panel that scrolls).
 * @param {Function} [deps.onDragStart]    Called once when a vertical drag locks.
 * @param {Function} [deps.onBeforeCommit] Called right before ANY navigation
 *                                         commit (host fires haptics, flushes UI).
 * @param {Function} deps.onCommit         (dir: 'next'|'prev') — called AFTER the
 *                                         settle animation completes (instantly
 *                                         under reduced motion); the host swaps
 *                                         the displayed item.
 */
export function useImmersiveSwipe({
  getPaneHeight,
  canNext,
  canPrev,
  shouldIgnore,
  onDragStart,
  onBeforeCommit,
  onCommit,
}) {
  // Reactive drag state — the track's class/style bindings consume these.
  const dragOffset = ref(0) // track translateY in px (negative = toward next)
  const isDragging = ref(false) // finger down AND vertically locked
  const isSettling = ref(false) // commit/spring-back transition running
  const dragDirection = ref(null) // 'next' | 'prev' | null

  // Per-gesture bookkeeping (plain object: mutated every touchmove, never rendered).
  const g = {
    active: false, // a touch sequence is being tracked
    locked: null, // null (undecided) | 'vertical' | 'horizontal'
    startX: 0,
    startY: 0,
    startTime: 0,
    lastY: 0,
    lastT: 0,
    velocity: 0, // low-pass filtered, px/ms (negative = upward)
    reduced: false, // prefers-reduced-motion snapshot for this gesture
  }
  let settleTimer = null

  function isInteractive(target) {
    return !!target?.closest?.(INTERACTIVE_SELECTOR)
  }

  function clearSettleTimer() {
    if (settleTimer) clearTimeout(settleTimer)
    settleTimer = null
  }

  function resetDragState() {
    isDragging.value = false
    dragOffset.value = 0
    dragDirection.value = null
  }

  // ── Touch handlers ───────────────────────────────────────────────
  function onTouchStart(e) {
    // A fast swiper waits out the ~280ms settle; interrupting mid-commit would
    // race the item swap. Interactive controls are never hijacked as swipes.
    if (isSettling.value) return
    if (!e.touches || e.touches.length !== 1) return
    if (isInteractive(e.target) || shouldIgnore?.(e.target)) return
    g.active = true
    g.locked = null
    g.startX = e.touches[0].clientX
    g.startY = e.touches[0].clientY
    g.startTime = Date.now()
    g.lastY = g.startY
    g.lastT = window.performance.now()
    g.velocity = 0
    g.reduced = prefersReducedMotion()
  }

  function onTouchMove(e) {
    if (!g.active || g.locked === 'horizontal') return
    // Reduced motion: no finger-follow — everything happens on release.
    if (g.reduced) return
    const touch = e.touches && e.touches[0]
    if (!touch) return
    const deltaX = touch.clientX - g.startX
    const deltaY = touch.clientY - g.startY

    // Axis lock: wait for clear intent, then commit to one axis per gesture.
    if (!g.locked) {
      if (Math.abs(deltaX) < LOCK_DISTANCE && Math.abs(deltaY) < LOCK_DISTANCE) return
      g.locked = Math.abs(deltaY) > Math.abs(deltaX) * LOCK_DOMINANCE ? 'vertical' : 'horizontal'
      if (g.locked === 'horizontal') return
      isDragging.value = true
      onDragStart?.()
    }

    // Vertically locked: stop any native scroll fallout (the template registers
    // touchmove WITHOUT .passive for exactly this call).
    if (e.cancelable) e.preventDefault()

    // Low-pass velocity (px/ms): smooth enough to ignore jitter, fresh enough
    // to read a genuine flick at release time.
    const now = window.performance.now()
    const dt = now - g.lastT
    if (dt > 0) {
      const v = (touch.clientY - g.lastY) / dt
      g.velocity = g.velocity === 0 ? v : v * 0.6 + g.velocity * 0.4
      g.lastY = touch.clientY
      g.lastT = now
    }

    // 1:1 finger follow; rubber-band when dragging toward a missing neighbor.
    const hasNeighbor = deltaY < 0 ? !!canNext?.() : !!canPrev?.()
    dragOffset.value = hasNeighbor ? deltaY : deltaY * EDGE_RESISTANCE
    dragDirection.value = deltaY < 0 ? 'next' : 'prev'
  }

  function onTouchEnd(e) {
    if (!g.active) return
    g.active = false

    // Reduced-motion fallback: the previous threshold-fire behavior, verbatim,
    // ending in an INSTANT swap.
    if (g.reduced) {
      const t0 = e.changedTouches && e.changedTouches[0]
      if (!t0) return
      const deltaX = t0.clientX - g.startX
      const deltaY = t0.clientY - g.startY
      if (Date.now() - g.startTime > RM_MAX_DURATION) return
      if (Math.abs(deltaY) < RM_SWIPE_THRESHOLD) return
      if (Math.abs(deltaY) < Math.abs(deltaX) * RM_DIRECTION_RATIO) return
      const dir = deltaY > 0 ? 'prev' : 'next'
      const hasNeighbor = dir === 'next' ? !!canNext?.() : !!canPrev?.()
      if (!hasNeighbor) return
      onBeforeCommit?.()
      onCommit?.(dir)
      return
    }

    if (g.locked !== 'vertical') return

    // Release physics: distance OR flick (velocity in the SAME direction as the
    // drag, past a small floor) commits; anything else springs back.
    const offset = dragOffset.value
    const dir = offset < 0 ? 'next' : 'prev'
    const paneH = Math.max(1, getPaneHeight?.() || window.innerHeight)
    const absOffset = Math.abs(offset)
    const flick = Math.abs(g.velocity) > COMMIT_VELOCITY && g.velocity * offset > 0
    const hasNeighbor = dir === 'next' ? !!canNext?.() : !!canPrev?.()
    const shouldCommit =
      hasNeighbor &&
      (absOffset > paneH * COMMIT_RATIO || (flick && absOffset > MIN_COMMIT_DISTANCE))

    if (shouldCommit) commitTo(dir, paneH)
    else springBack()
  }

  // Finger taken by the system (call, notification shade, ...) — never commit.
  function onTouchCancel() {
    if (!g.active) return
    g.active = false
    if (g.locked === 'vertical') springBack()
  }

  // ── Release animations ───────────────────────────────────────────
  function commitTo(dir, paneH) {
    isDragging.value = false
    isSettling.value = true
    dragDirection.value = dir
    onBeforeCommit?.()
    // Slide a full pane so the neighbor settles into the viewport...
    dragOffset.value = dir === 'next' ? -paneH : paneH
    clearSettleTimer()
    settleTimer = setTimeout(() => {
      settleTimer = null
      // ...then swap the item and re-center in the SAME Vue patch with the
      // transition class removed: the browser paints the swapped current pane
      // at translate 0 exactly where the neighbor just was — no visible jump.
      onCommit?.(dir)
      isSettling.value = false
      resetDragState()
    }, SETTLE_MS + SETTLE_BUFFER_MS)
  }

  function springBack() {
    isDragging.value = false
    if (dragOffset.value === 0) {
      resetDragState()
      return
    }
    isSettling.value = true
    dragOffset.value = 0
    clearSettleTimer()
    settleTimer = setTimeout(() => {
      settleTimer = null
      isSettling.value = false
      dragDirection.value = null
    }, SETTLE_MS + SETTLE_BUFFER_MS)
  }

  // Programmatic navigation (wheel / keyboard / chevrons): the same settle
  // spring the finger gets. Nudges at edges. Returns true when a commit ran.
  function commit(dir) {
    if (isDragging.value || isSettling.value) return false
    const hasNeighbor = dir === 'next' ? !!canNext?.() : !!canPrev?.()
    if (!hasNeighbor) {
      nudge(dir)
      return false
    }
    if (prefersReducedMotion()) {
      onBeforeCommit?.()
      onCommit?.(dir)
      return true
    }
    commitTo(dir, Math.max(1, getPaneHeight?.() || window.innerHeight))
    return true
  }

  // Programmatic edge feedback: reuse the settle spring as a small out-and-back
  // nudge in the blocked direction. No-op mid-gesture and under reduced motion.
  function nudge(dir) {
    if (isDragging.value || isSettling.value || prefersReducedMotion()) return
    isSettling.value = true
    dragOffset.value = dir === 'next' ? -NUDGE_PX : NUDGE_PX
    clearSettleTimer()
    settleTimer = setTimeout(() => {
      dragOffset.value = 0
      settleTimer = setTimeout(() => {
        settleTimer = null
        isSettling.value = false
      }, SETTLE_MS + SETTLE_BUFFER_MS)
    }, NUDGE_HOLD_MS)
  }

  // Post-jump "scroll into place" for ARBITRARY index jumps (sub-category
  // selector / double chevrons): the host has already swapped to the target
  // item, so the neighbor pane in the travel direction is on screen when the
  // offset is preset; animating to 0 reads as one pane scrolling in from
  // `dir` ('next' = arrives from below). Reduced motion keeps the plain swap.
  function settleFrom(dir) {
    if (isDragging.value || prefersReducedMotion()) return
    clearSettleTimer()
    const paneH = Math.max(1, getPaneHeight?.() || window.innerHeight)
    // Preset WITHOUT the transition class, then animate on the next frame
    // (double rAF: the preset must paint before the transition turns on).
    isSettling.value = false
    dragOffset.value = dir === 'next' ? paneH : -paneH
    dragDirection.value = dir
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        isSettling.value = true
        dragOffset.value = 0
        settleTimer = setTimeout(() => {
          settleTimer = null
          isSettling.value = false
          dragDirection.value = null
        }, SETTLE_MS + SETTLE_BUFFER_MS)
      })
    })
  }

  // Hard reset (host calls on overlay close/unmount): drop any in-flight
  // gesture or settle WITHOUT firing commit callbacks.
  function resetSwipe() {
    g.active = false
    g.locked = null
    clearSettleTimer()
    isSettling.value = false
    resetDragState()
  }

  return {
    dragOffset,
    isDragging,
    isSettling,
    dragDirection,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
    commit,
    nudge,
    settleFrom,
    resetSwipe,
  }
}
